/**
 * Ошибка слоя данных — порт `DataError` Android-версии.
 *
 * Репозиторий остаётся границей ошибок: наружу не выходят исключения SQLite,
 * файловой системы и прочие подробности хранилища.
 */
export type DataError =
  | { kind: 'NotFound'; id: string }
  | { kind: 'Conflict'; reason: string }
  | { kind: 'Storage'; reason: string }
  | { kind: 'OutOfSpace' }
  | { kind: 'MissingFile'; fileName: string };

export type DataResult<T> = { ok: true; value: T } | { ok: false; error: DataError };

export const success = <T>(value: T): DataResult<T> => ({ ok: true, value });
export const failure = <T = never>(error: DataError): DataResult<T> => ({ ok: false, error });

/** Текст ошибки для человека. Один на приложение — иначе разойдётся по экранам. */
export function describe(error: DataError): string {
  switch (error.kind) {
    case 'NotFound':
      return 'Запись не найдена';
    case 'Conflict':
      return error.reason;
    case 'OutOfSpace':
      return 'На устройстве не хватает места';
    case 'MissingFile':
      return 'Файл документа не найден';
    case 'Storage':
      return 'Не удалось сохранить';
  }
}

/**
 * Оборачивает работу с хранилищем: исключение превращается в ошибку данных.
 *
 * Нехватка места распознаётся отдельно — на iPhone это самый вероятный сбой
 * при съёмке документов, и «не удалось сохранить» юристу ничего не объяснит.
 */
export async function guard<T>(action: () => Promise<T>): Promise<DataResult<T>> {
  try {
    return success(await action());
  } catch (error) {
    const reason = error instanceof Error ? error.message : String(error);

    if (/quota|space|full/i.test(reason)) return failure({ kind: 'OutOfSpace' });
    return failure({ kind: 'Storage', reason });
  }
}
